import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useVerifyEmail, useResendVerificationEmail } from "@/api/AuthApi";

type VerificationStatus = "loading" | "success" | "error";

const resendSchema = z.object({
  email: z.string().email("Veuillez entrer une adresse email valide."),
});

type ResendFormData = z.infer<typeof resendSchema>;

const VerifyEmail: React.FC = () => {
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>();
  const navigate = useNavigate();
  const { verifyEmail } = useVerifyEmail();

  const [status, setStatus] = useState<VerificationStatus>("loading");
  const [message, setMessage] = useState<string>("");
  const hasRun = useRef(false);

  const { resendVerificationEmail, loading: resendLoading } =
    useResendVerificationEmail();
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const {
    register: registerResend,
    handleSubmit: handleResendSubmit,
    formState: { errors: resendErrors },
  } = useForm<ResendFormData>({
    resolver: zodResolver(resendSchema),
  });

  const onResendSubmit = async (data: ResendFormData) => {
    const result = await resendVerificationEmail(data.email);
    if (result.success) {
      setResendSent(true);
    }
  };

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const runVerification = async () => {
      if (!uidb64 || !token) {
        setStatus("error");
        setMessage("Lien de vérification invalide.");
        return;
      }

      const result = await verifyEmail(uidb64, token);
      if (result.success) {
        setStatus("success");
        setMessage(result.detail);
      } else {
        setStatus("error");
        const data = result.error?.response?.data;
        const detail =
          typeof data === "string"
            ? data
            : typeof data?.detail === "string"
              ? data.detail
              : undefined;
        setMessage(
          detail || "Ce lien de vérification est invalide ou a expiré.",
        );
      }
    };

    runVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uidb64, token]);

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      <div className="absolute top-6 left-8 flex items-center space-x-2">
        <img
          src="../images/RAJAPI_logo.png"
          alt="Rajapi Logo"
          className="w-8 h-8"
        />
        <span className="text-xl font-extrabold text-blue-500">RAJAPI-COP</span>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-lg max-w-sm w-full text-center">
        {status === "loading" && (
          <>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500" />
            <h2 className="text-lg font-semibold">
              Vérification de votre compte...
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Merci de patienter pendant que nous vérifions votre adresse email.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <FaCheckCircle className="text-green-500 text-5xl mx-auto mb-4" />
            <h2 className="text-lg font-semibold">
              Email vérifié avec succès !
            </h2>
            <p className="text-sm text-gray-600 mt-2">{message}</p>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full mt-6 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg"
            >
              Se connecter
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <FaTimesCircle className="text-red-500 text-5xl mx-auto mb-4" />
            <h2 className="text-lg font-semibold">Échec de la vérification</h2>
            <p className="text-sm text-gray-600 mt-2">{message}</p>

            {resendSent ? (
              <p className="text-sm text-green-600 mt-4">
                Si un compte existe avec cette adresse, un nouvel email de
                vérification vient de vous être envoyé.
              </p>
            ) : showResendForm ? (
              <form
                onSubmit={handleResendSubmit(onResendSubmit)}
                className="mt-4 text-left"
              >
                <label
                  htmlFor="resend-email"
                  className="text-sm font-medium text-gray-600"
                >
                  Votre adresse email
                </label>
                <input
                  id="resend-email"
                  type="email"
                  placeholder="exemple@gmail.com"
                  {...registerResend("email")}
                  className={`w-full mt-1 p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    resendErrors.email
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {resendErrors.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {resendErrors.email.message}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={resendLoading}
                  className="w-full mt-3 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg disabled:opacity-60"
                >
                  {resendLoading ? "Envoi..." : "Renvoyer l'email"}
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowResendForm(true)}
                className="text-sm text-blue-500 hover:text-blue-700 mt-4"
              >
                Renvoyer l'email de vérification
              </button>
            )}

            <div className="flex flex-col gap-3 mt-6">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg"
              >
                Retour à la connexion
              </button>
            </div>
          </>
        )}
      </div>

      <footer className="absolute bottom-4 text-gray-400 text-sm">
        © 2024 Kiosk
      </footer>
    </div>
  );
};

export default VerifyEmail;
